import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
import { cn } from '../lib/utils';

const Navigation: React.FC = React.memo(() => {
  const prefersReducedMotion = useReducedMotion();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [dropdownTimeout, setDropdownTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useTranslation();
  const location = useLocation();

  const currentLang = useMemo(() =>
    location.pathname.startsWith('/en') ? 'en' : 'de',
    [location.pathname]
  );

  const navigationItems = useMemo(() => [
    { name: t('nav.home'), href: `/${currentLang}` },
    { name: t('nav.about'), href: `/${currentLang}/about` },
    {
      name: t('nav.services'),
      href: `/${currentLang}/services`,
      dropdown: [
        {
          name: t('services.build.navTitle'),
          subtitle: t('services.build.navSubtitle'),
          href: `/${currentLang}/services/business-consulting`
        },
        {
          name: t('services.scale.navTitle'),
          subtitle: t('services.scale.navSubtitle'),
          href: `/${currentLang}/services/ocpd-executive-coaching`
        },
        {
          name: t('services.invest.navTitle'),
          subtitle: t('services.invest.navSubtitle'),
          href: `/${currentLang}/services/legacy-wealth`
        }
      ]
    },
    { name: t('nav.blog'), href: `/${currentLang}/blog` },
    { name: t('nav.contact'), href: `/${currentLang}/contact` }
  ], [t, currentLang]);

  const isActive = useCallback((href: string) => {
    if (href === `/${currentLang}`) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  }, [currentLang, location.pathname]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  // Handle dropdown hover with delay - memoized for performance
  const handleDropdownEnter = useCallback((index: number) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setOpenDropdown(index);
  }, [dropdownTimeout]);

  const handleDropdownLeave = useCallback(() => {
    // Add a small delay before closing to allow mouse movement
    const timeout = setTimeout(() => {
      setOpenDropdown(null);
    }, 150); // 150ms delay
    setDropdownTimeout(timeout);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-[100]"
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Desktop Glass Navbar - Dark Blue Theme */}
      <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-xl border-b border-white/10 shadow-lg transition-all duration-300" />

      <div className="relative max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex-shrink-0 z-50">
            <Link to={`/${currentLang}`} className="flex items-center hover:opacity-80 transition-opacity" onClick={closeMobileMenu}>
              <img
                src="/logo-white.png"
                alt="Plaschy Treuhand Beratungen GmbH"
                className="h-11 w-auto"
                width="768"
                height="768"
              />
            </Link>
          </div>

          {/* Desktop Navigation Menu */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-center space-x-1">
              {navigationItems.map((item, index) => (
                <div
                  key={index}
                  className="relative"
                >
                  {item.dropdown ? (
                    <div
                      className="relative"
                      onMouseEnter={() => handleDropdownEnter(index)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <button
                        className={cn(
                          "px-4 py-2 text-sm font-medium flex items-center gap-1 transition-all duration-300",
                          isActive(item.href)
                            ? "bg-white/10 text-white"
                            : "text-slate-300 hover:bg-white/10 hover:text-white"
                        )}
                        type="button"
                      >
                        {item.name}
                        <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", openDropdown === index ? "rotate-180" : "")} />
                      </button>

                      <AnimatePresence>
                        {openDropdown === index && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute left-0 top-full pt-2 w-72 z-[10000]"
                            onMouseEnter={() => handleDropdownEnter(index)}
                            onMouseLeave={handleDropdownLeave}
                          >
                            <div className="bg-white/95 backdrop-blur-xl shadow-2xl border border-white/20 overflow-hidden ring-1 ring-black/5">
                              <div className="py-2">
                                {item.dropdown.map((subItem, subIndex) => (
                                  <Link
                                    key={subIndex}
                                    to={subItem.href}
                                    className="block px-6 py-3 hover:bg-slate-50 transition-colors group"
                                    onClick={() => setOpenDropdown(null)}
                                  >
                                    <div className="text-sm font-bold text-slate-900 group-hover:text-accent-orange transition-colors">
                                      {subItem.name}
                                    </div>
                                    <div className="text-xs text-slate-500 font-medium mt-0.5">
                                      {subItem.subtitle}
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        "px-4 py-2 text-sm font-medium transition-all duration-300",
                        isActive(item.href)
                          ? "bg-white/10 text-white"
                          : "text-slate-300 hover:bg-white/10 hover:text-white"
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Language Switcher - Desktop */}
          <div className="hidden lg:flex items-center">
            <LanguageSwitcher />
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden z-50">
            <button
              onClick={toggleMobileMenu}
              className="p-2 bg-white/10 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 transition-all shadow-sm"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[90] lg:hidden"
              onClick={closeMobileMenu}
            />

            {/* Menu Card */}
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.95 }}
              animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.95 }}
              transition={prefersReducedMotion ? { duration: 0.15 } : { type: "spring", duration: 0.5, bounce: 0.15 }}
              className="fixed top-20 left-4 right-4 z-[100] lg:hidden outline-none"
            >
              <div className="bg-white/90 backdrop-blur-2xl shadow-2xl border border-white/40 overflow-hidden ring-1 ring-black/5">
                <div className="px-6 py-6 space-y-2 max-h-[80vh] overflow-y-auto">
                  {navigationItems.map((item, index) => (
                    <div key={index}>
                      {item.dropdown ? (
                        <div className="space-y-1">
                          <div className="px-4 py-2 text-sm font-bold text-slate-400 uppercase tracking-wider">
                            {item.name}
                          </div>
                          <div className="pl-4 space-y-1 border-l-2 border-slate-100 ml-4">
                            {item.dropdown.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.href}
                                className="block px-4 py-3 transition-all hover:bg-white/50 group"
                                onClick={closeMobileMenu}
                              >
                                <div className="text-base font-bold text-slate-900 group-hover:text-accent-orange transition-colors">
                                  {subItem.name}
                                </div>
                                <div className="text-sm text-slate-500 font-medium mt-0.5">
                                  {subItem.subtitle}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={item.href}
                          className={cn(
                            "block px-4 py-3 text-lg font-medium transition-all",
                            isActive(item.href)
                              ? "bg-slate-900/5 text-slate-900"
                              : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                          )}
                          onClick={closeMobileMenu}
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}

                  <div className="pt-4 mt-4 border-t border-slate-200/50">
                    <div className="px-4">
                      <LanguageSwitcher />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
});

Navigation.displayName = 'Navigation';

export default Navigation;
